import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, X, Send, Users } from 'lucide-react';

/**
 * Site-wide thin announcement strip that sits on top of the navbar.
 * Auto-hides on `/staymii-hr` so it doesn't promote the page you're already on.
 * Dismissible via localStorage flag `hr_promo_strip_dismissed`.
 */
export function HRPromoStrip() {
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    const dismissed = localStorage.getItem('hr_promo_strip_dismissed') === '1';
    const onHRPage = window.location.pathname.startsWith('/staymii-hr')
      || window.location.pathname.startsWith('/free-hr-system-malaysia')
      || window.location.pathname.startsWith('/hr-application-form')
      || window.location.pathname.startsWith('/free-job-application-form')
      || window.location.pathname.startsWith('/hr-recruitment-form')
      || window.location.pathname.startsWith('/job-application-form-malaysia')
      || window.location.pathname.startsWith('/online-job-application-form');
    setHidden(dismissed || onHRPage);
  }, []);

  if (hidden) return null;

  return (
    <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 text-white text-xs sm:text-sm relative z-[60]" data-testid="hr-promo-strip">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 shrink-0 text-amber-200" />
          <p className="truncate">
            <span className="font-bold">NEW · Free for SMEs:</span>{' '}
            <span className="hidden sm:inline">Generate your public HR application form in 60 seconds. Telegram alerts for every new applicant.</span>
            <span className="sm:hidden">Free HR application form for SMEs.</span>
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/staymii-hr"
            className="inline-flex items-center gap-1 font-bold underline decoration-amber-200 underline-offset-2 hover:text-amber-100 whitespace-nowrap"
            data-testid="hr-promo-strip-cta"
          >
            Create Free Form <ArrowRight className="w-3 h-3" />
          </Link>
          <button
            onClick={() => { localStorage.setItem('hr_promo_strip_dismissed', '1'); setHidden(true); }}
            aria-label="Dismiss"
            className="opacity-70 hover:opacity-100"
            data-testid="hr-promo-strip-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * In-page promotional card. Drop into any landing page section.
 * Highlights free HR module with prominent CTA.
 */
export function HRPromoCard() {
  return (
    <section className="py-16 lg:py-20 bg-white" data-testid="hr-promo-card-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-[#0B1120] rounded-3xl overflow-hidden border border-emerald-500/20 p-8 sm:p-12 lg:p-14">
          {/* glow accents */}
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] bg-emerald-500/15 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] bg-teal-500/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-300 text-xs font-bold mb-5">
                <Sparkles className="w-3.5 h-3.5" /> 100% FREE · NO CREDIT CARD
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-white leading-[1.1] mb-4 tracking-[-0.02em]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
                Free HR Application Form for{' '}
                <span className="text-emerald-400">Malaysian SMEs</span>
              </h2>
              <p className="text-white/70 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
                Generate a public job application form link in 60 seconds — share it on
                WhatsApp, FB, IG bio, or JobStreet. Get instant Telegram alerts when
                applicants apply. No setup fee. Forever free.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <Link
                  to="/staymii-hr"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-[#0B1120] rounded-xl font-bold text-sm transition-all shadow-lg shadow-emerald-500/20"
                  data-testid="hr-promo-card-cta-primary"
                >
                  Create Free HR Form <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <Link
                  to="/auth/login"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/5 text-white border border-white/20 hover:bg-white/10 rounded-xl font-bold text-sm transition-all"
                  data-testid="hr-promo-card-cta-secondary"
                >
                  Login to StayMii HR
                </Link>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/50 font-mono">
                <span>✓ FREE FOREVER</span>
                <span>✓ TELEGRAM ALERTS</span>
                <span>✓ MOBILE-FRIENDLY</span>
                <span>✓ PDPA-READY</span>
              </div>
            </div>

            {/* Mini mock */}
            <div className="lg:col-span-5">
              <div className="bg-[#0F172A] border border-white/10 rounded-2xl p-5 shadow-2xl">
                <div className="flex items-center gap-2 mb-4 text-[10px] font-mono text-white/40">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-amber-300" />
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="ml-2 truncate">staymii.com/hr/your-company/apply</span>
                </div>
                <div className="space-y-2.5 mb-3">
                  {['Full Name', 'IC / Passport', 'Position', 'Phone'].map((l) => (
                    <div key={l}>
                      <div className="text-[9px] font-mono text-white/40 mb-1 tracking-wider">{l.toUpperCase()}</div>
                      <div className="h-6 rounded-md bg-white/5 border border-white/10" />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <Send className="w-4 h-4 text-emerald-400" />
                  <span className="text-[11px] text-emerald-300 font-semibold">+ Telegram alert on submit</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-[11px] text-white/50">
                <Users className="w-3.5 h-3.5" />
                <span>Built for hotels, homestays, cafés, retail & SMEs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HRPromoCard;
